import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import GlowButton from "../components/GlowButton";
import HeroDashboardMockup from "../components/HeroDashboardMockup";
import AnimatedHeroTitle from "../components/AnimatedHeroTitle";
import MarketingBackdrop from "../components/MarketingBackdrop";
import WhyDifferentSection from "../components/WhyDifferentSection";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { AuthContext } from "../context/AuthContext";

// Agent chip color → Tailwind classes. Matches the Council palette used
// in WhyDifferentSection.jsx and the highlight chips above so visual
// language stays consistent across the page.
const AGENT_COLORS = {
  Lens: "bg-sky-500/15 text-sky-500 border-sky-500/40",
  Tally: "bg-amber-500/15 text-amber-500 border-amber-500/40",
  Vigil: "bg-rose-500/15 text-rose-500 border-rose-500/40",
  Compass: "bg-emerald-500/15 text-emerald-500 border-emerald-500/40",
  Anchor: "bg-indigo-500/15 text-indigo-500 border-indigo-500/40",
};

// Demo conversations shown in the "See it in action" section. Each tile
// shows a different combination of specialists so visitors see the range
// of questions the Council handles.
const CHAT_EXAMPLES = [
  {
    label: "Tax & risk",
    accent: "#a855f7",
    question: "Should I sell my NVDA. what's the tax and risk impact?",
    response:
      "NVDA is up 84% YTD with a P/E of 43 : Lens flags it as expensive at this level. Selling now would lock a long-term gain (12.5% LTCG above your ₹1.25L exemption : Tally). Vigil notes NVDA is 18% of your portfolio; trimming half cuts your single-name concentration risk meaningfully. Net: trim 50% feels defensible given your conservative profile and 3-year house goal.",
    agents: ["Lens", "Tally", "Vigil"],
    cite: "used live quote · fundamentals · tax rules · concentration",
  },
  {
    label: "Concentration",
    accent: "#10b981",
    question: "Am I too concentrated in tech?",
    response:
      "Your tech sleeve is 41%, Compass flags this is well above the 25% target for your conservative profile. Three names (NVDA, MSFT, AAPL) drive 80% of that exposure, and Vigil's correlation matrix shows them moving together at 0.82 effectively one bet. Rebalancing 16pp into international + bonds brings you to target without forcing a full exit on any winner.",
    agents: ["Compass", "Vigil"],
    cite: "used allocation targets · correlation matrix · live weights",
  },
  {
    label: "Goal planning",
    accent: "#f59e0b",
    question: "Can I afford a ₹80L down payment in 3 years?",
    response:
      "Your liquid equity is ₹2.1Cr. Compass projects ₹2.45Cr in 3 years at your current 11% blended return (60% confidence band ₹2.1–2.8Cr). After ₹80L down, you'd retain ₹1.65Cr equity plus your real estate. Vigil's 25% drawdown scenario still leaves ₹1.58Cr post-purchase fundable, with reasonable margin.",
    agents: ["Compass", "Vigil"],
    cite: "used expected return · drawdown sim · stated goal",
  },
  {
    label: "Tax harvesting",
    accent: "#3b82f6",
    question: "Any tax-loss harvesting opportunities right now?",
    response:
      "Tally found three positions in loss: ZOMATO (–₹38K, held 14 months · LTCL), PAYTM (–₹52K, held 22 months · LTCL), and DMART (–₹19K, held 8 months · STCL). Harvesting offsets ₹62K of your YTD short-term gains saves ₹19K at your 30% bracket. India has no wash-sale, so you can rebuy immediately.",
    agents: ["Tally", "Lens"],
    cite: "used cost basis · holding periods · bracket lookup",
  },
];

// Compact onboarding mockup — brokerage-linked indicator + asset
// allocation bars + profile tags. Shows what step 1 produces.
const ConnectMockup = () => {
  const assets = [
    { label: "Stocks & ETFs", pct: 58, color: "#10b981" },
    { label: "Real estate", pct: 18, color: "#a855f7" },
    { label: "Gold", pct: 11, color: "#f59e0b" },
    { label: "Crypto", pct: 7, color: "#3b82f6" },
    { label: "FDs · Bonds", pct: 6, color: "#6366f1" },
  ];
  return (
    <div className="bg-black/30 border border-white/5 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between text-[11px]">
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-gray-300 font-medium">
            Brokerage linked · SnapTrade
          </span>
        </div>
        <span className="text-gray-500">+ Add</span>
      </div>
      <div className="space-y-1.5">
        {assets.map(({ label, pct, color }) => (
          <div key={label}>
            <div className="flex items-center justify-between text-[10px] mb-0.5">
              <span className="text-gray-400">{label}</span>
              <span className="font-semibold text-gray-200 tabular-nums">
                {pct}%
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{ width: `${pct}%`, backgroundColor: color }}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="pt-2 border-t border-white/5 flex flex-wrap gap-1">
        {["Conservative", "3-yr goal", "India", "No oil/coal"].map((tag) => (
          <span
            key={tag}
            className="px-1.5 py-0.5 rounded-md bg-white/5 border border-white/10 text-[9px] text-gray-300"
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
};

// Compact chat mockup — user question + Council reply with agent
// attribution chips. Shows what step 2 looks like in use.
const ChatMockup = () => (
  <div className="bg-black/30 border border-white/5 rounded-xl p-4 space-y-3">
    <div className="flex justify-end">
      <div className="px-3 py-1.5 rounded-2xl rounded-br-sm bg-indigo-600 text-white text-xs max-w-[78%]">
        Should I trim NVDA?
      </div>
    </div>
    <div>
      <div className="px-3 py-2 rounded-2xl rounded-tl-sm bg-white/5 border border-white/10 text-xs text-gray-200 max-w-[88%] leading-relaxed">
        Trim 50%. P/E at 43 is rich, 18% concentration is too high, and you'd
        lock LTCG below your exemption.
      </div>
      <div className="flex flex-wrap gap-1 mt-1.5">
        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full border bg-sky-500/15 text-sky-500 border-sky-500/40">
          Lens
        </span>
        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full border bg-rose-500/15 text-rose-500 border-rose-500/40">
          Vigil
        </span>
        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full border bg-amber-500/15 text-amber-500 border-amber-500/40">
          Tally
        </span>
      </div>
    </div>
    <p className="text-[9px] text-gray-500 pt-0.5">
      Answered in 1.2s · used live quote · concentration · tax rules
    </p>
  </div>
);

// Compact daily-brief mockup — header strip + 5 dotted bullets.
const BriefMockup = () => {
  const bullets = [
    { dot: "#10b981", text: "Portfolio +1.2% overnight on Fed minutes" },
    { dot: "#3b82f6", text: "TSLA breaks 200d MA — Lens flags caution" },
    { dot: "#f59e0b", text: "Tax-loss available: ZOMATO −₹38K · LTCL" },
    { dot: "#a855f7", text: "Concentration check: tech 41% vs 25% target" },
    { dot: "#f43f5e", text: "Action: trim 5% TSLA → bonds for balance" },
  ];
  return (
    <div className="bg-black/30 border border-white/5 rounded-xl p-4">
      <div className="flex items-center justify-between text-[10px] text-gray-400 mb-3">
        <span className="font-bold uppercase tracking-[0.18em]">
          Morning brief
        </span>
        <span>Tue · 9 Dec</span>
      </div>
      <div className="space-y-2">
        {bullets.map(({ dot, text }, i) => (
          <div
            key={i}
            className="flex items-start gap-2 text-[11px] text-gray-300 leading-relaxed"
          >
            <span
              className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
              style={{ backgroundColor: dot }}
            />
            <span>{text}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const STEPS = [
  {
    n: "01",
    accent: "#10b981",
    title: "Connect & profile",
    body:
      "Sync a brokerage in 30 seconds via SnapTrade. Add the messier stuff real estate, gold, FDs, crypto. Tell us your risk appetite, goal, and tax bracket in four screens.",
    visual: <ConnectMockup />,
  },
  {
    n: "02",
    accent: "#6366f1",
    title: "Ask in plain English",
    body:
      "Tax? Risk? Single stock? Allocation? Anchor decides which specialists to call and stitches their answers into one reply with citations, in seconds.",
    visual: <ChatMockup />,
  },
  {
    n: "03",
    accent: "#f59e0b",
    title: "Wake up to the brief",
    body:
      "Every morning: 5 bullets with real numbers from your real holdings. One thing worth doing today. Tap any line to dig deeper with the Council.",
    visual: <BriefMockup />,
  },
];

const Home = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const ctaHref = isAuthenticated ? "/portfolio" : "/signup";
  const ctaLabel = isAuthenticated ? "Go to Dashboard" : "Get started — it's free";

  // Active example in the "See it in action" section. Switched by the
  // pagination dots / arrows below the chat card.
  const [activeChat, setActiveChat] = useState(0);
  const chat = CHAT_EXAMPLES[activeChat];

  return (
    <div className="relative isolate min-h-screen flex flex-col bg-white dark:bg-[#060B12] text-gray-900 dark:text-white transition-colors">
      <SEO
        path="/"
        title="FinAI — AI portfolio analysis & a council of AI advisors"
        bareTitle
        description="A board of AI specialists for your money. Track everything you own, analyze your portfolio vs. the S&P 500, get daily market briefings, and ask anything. Free to start."
      />
      <MarketingBackdrop />
      <Navbar />

      {/* HERO */}
      <section className="relative pt-32 pb-24">
        <div className="relative container mx-auto px-6 text-center max-w-5xl">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/60 dark:bg-white/5 border border-gray-200 dark:border-white/10 backdrop-blur-sm mb-8">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            <span className="text-xs font-semibold tracking-wide text-gray-700 dark:text-gray-200">
              The Council is live · 5 specialist agents
            </span>
          </div>

          <AnimatedHeroTitle />
          <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-200 font-semibold mb-2">
            Track everything you own. Ask anything.
          </p>
          <p className="text-base md:text-lg text-gray-500 dark:text-gray-400 mb-10">
            Five AI specialists. One conversation. Free to start.
          </p>

          <div className="flex justify-center mb-14">
            <GlowButton to={ctaHref} size="lg">
              {ctaLabel.toUpperCase()}
            </GlowButton>
          </div>
        </div>

        {/* Mockup gets its own wider wrapper so it can breathe. */}
        <div className="relative container mx-auto px-4 md:px-8 mt-2">
          <HeroDashboardMockup />
        </div>
      </section>

      <WhyDifferentSection />

      {/* HOW IT WORKS */}
      <section className="relative py-20 lg:py-28">
        <div className="container mx-auto px-6 max-w-[1500px] relative">
          {/* Left-aligned header */}
          <div className="mb-12 lg:mb-16 max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-500 mb-4">
              How it works
            </p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 leading-[1.05]">
              From sign-up to a daily brief in three steps.
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-base lg:text-lg">
              No advisor. No quarterly review. No 1% AUM fee.
            </p>
          </div>

          {/* 3-card grid — each card carries a numbered label, a heading,
              a short body, and a visual mockup of what the step produces. */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-6">
            {STEPS.map((step) => (
              <div
                key={step.n}
                className="relative rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-50/60 dark:bg-[#0a0f1a]/60 backdrop-blur-sm p-6 lg:p-7 transition-all duration-500 hover:border-gray-300 dark:hover:border-white/20 hover:shadow-2xl hover:shadow-black/20"
              >
                <p
                  className="text-xs font-bold tracking-[0.22em] mb-5"
                  style={{ color: step.accent }}
                >
                  {step.n}
                </p>
                <h3 className="text-2xl font-extrabold mb-3 text-gray-900 dark:text-white leading-tight">
                  {step.title}
                </h3>
                <p className="text-gray-500 dark:text-gray-400 leading-relaxed mb-6 text-sm lg:text-[15px]">
                  {step.body}
                </p>
                {step.visual}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CHAT MOCKUP */}
      <section className="relative py-20 lg:py-28">
        <div className="container mx-auto px-6 max-w-4xl relative">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-widest text-purple-500 mb-3">
              See it in action
            </p>
            <h2 className="text-3xl md:text-4xl font-extrabold">
              Real questions. Real numbers. Real answers.
            </h2>
          </div>

          {/* Chat card — fixed min-height so different example responses
              don't change the card's footprint and shift the dots below.
              key drives a remount so the existing animate-fadeIn keyframe
              replays on each switch. */}
          <div
            key={activeChat}
            className="animate-fadeIn rounded-2xl bg-white/60 dark:bg-white/[0.03] border border-gray-200 dark:border-white/10 p-6 backdrop-blur-sm min-h-[22rem] flex flex-col"
          >
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-4">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="font-semibold">FinAI · the Council</span>
            </div>

            <div className="space-y-4">
              {/* User question */}
              <div className="flex justify-end">
                <div className="px-4 py-2.5 rounded-2xl rounded-br-sm bg-indigo-600 text-white text-sm max-w-[85%]">
                  {chat.question}
                </div>
              </div>

              {/* Council response with multi-agent attribution */}
              <div className="max-w-[88%]">
                <div className="px-4 py-3 rounded-2xl rounded-tl-sm bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-sm leading-relaxed">
                  {chat.response}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {chat.agents.map((agent) => (
                    <span
                      key={agent}
                      className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${AGENT_COLORS[agent]}`}
                    >
                      {agent}
                    </span>
                  ))}
                </div>
                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
                  ↳ {chat.cite}
                </p>
              </div>
            </div>
          </div>

          {/* Carousel pagination — muted prev/next chevrons flanking the
              dot indicators. Active dot expands into a colored pill;
              inactive dots are 6px circles. Wraps around at both ends. */}
          <div className="mt-8 flex justify-center items-center gap-5">
            <button
              type="button"
              aria-label="Previous example"
              onClick={() =>
                setActiveChat(
                  (p) => (p - 1 + CHAT_EXAMPLES.length) % CHAT_EXAMPLES.length
                )
              }
              className="text-gray-400/60 dark:text-white/25 hover:text-gray-700 dark:hover:text-white/70 transition-colors p-1"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>

            <div className="flex items-center gap-3">
              {CHAT_EXAMPLES.map((ex, i) => {
                const isActive = activeChat === i;
                return (
                  <button
                    key={ex.label}
                    type="button"
                    title={ex.label}
                    aria-label={`Show example: ${ex.label}`}
                    onClick={() => setActiveChat(i)}
                    className={`h-1.5 rounded-full transition-all duration-500 ease-out ${
                      isActive
                        ? "w-10"
                        : "w-1.5 bg-gray-300/60 dark:bg-white/15 hover:w-3 hover:bg-gray-400 dark:hover:bg-white/40"
                    }`}
                    style={
                      isActive
                        ? {
                            background: `linear-gradient(90deg, ${ex.accent}, ${ex.accent}b3)`,
                            boxShadow: `0 0 16px ${ex.accent}66, 0 0 4px ${ex.accent}33`,
                          }
                        : undefined
                    }
                  />
                );
              })}
            </div>

            <button
              type="button"
              aria-label="Next example"
              onClick={() =>
                setActiveChat((p) => (p + 1) % CHAT_EXAMPLES.length)
              }
              className="text-gray-400/60 dark:text-white/25 hover:text-gray-700 dark:hover:text-white/70 transition-colors p-1"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      <Footer ctaHref={ctaHref} ctaLabel={ctaLabel} />
    </div>
  );
};

export default Home;
